import React, { useState, useEffect, useMemo, useRef } from 'react';
import styles from '../../../styles/EstudiantesStyles/Estudiantes.module.css';
import { generateCredencialPDF } from '../../../utils/generateCredencialPDF';
import { getDocumentosEstudiante, subirFotoEstudiante, getUrlDescargaDocEstudiante } from '../../../api/students';

function ExpedienteAlumno({ student, fotoUrl, onFotoChanged }) {
  const estudianteId = student?.id_estudiante || student?.id;
  const [docs, setDocs] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [fotoUrlLocal, setFotoUrlLocal] = useState('');
  const [fotoUrlAuth, setFotoUrlAuth] = useState('');
  const [fotoLoadError, setFotoLoadError] = useState(false);
  const fotoInputRef = useRef(null);

  const getApiBase = () => {
    const envBase = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');
    if (envBase) return envBase;
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}/api`;
    }
    return '';
  };

  const fotoPerfilDoc = useMemo(() => {
    const list = Array.isArray(docs) && docs.length > 0
      ? docs
      : (Array.isArray(student?.documentos) ? student.documentos : []);
    return list.find(d => d.tipo_documento === 'FOTO_PERFIL') || list.find(d => d.tipo_documento === 'PROFILE');
  }, [docs, student]);

  const fotoDocUrl = useMemo(() => {
    if (fotoPerfilDoc?.archivo_url) return fotoPerfilDoc.archivo_url;
    if (fotoPerfilDoc?.url) {
      const mediaBase = getApiBase().replace(/\/api$/, '');
      return `${mediaBase}${fotoPerfilDoc.url}`;
    }
    if (fotoPerfilDoc?.ruta_archivo) {
      const mediaBase = getApiBase().replace(/\/api$/, '');
      return `${mediaBase}/media/${fotoPerfilDoc.ruta_archivo}`;
    }
    return '';
  }, [fotoPerfilDoc]);

  const fotoPerfilUrl = useMemo(() => {
    if (fotoUrlLocal) return fotoUrlLocal;
    if (fotoUrlAuth) return fotoUrlAuth;
    if (fotoDocUrl) return fotoDocUrl;
    if (fotoUrl) return fotoUrl;
    return '';
  }, [fotoUrlLocal, fotoUrlAuth, fotoDocUrl, fotoUrl]);

  useEffect(() => {
    setFotoLoadError(false);
  }, [fotoPerfilUrl]);

  useEffect(() => {
    let active = true;
    let objectUrl = '';

    const tryFetchImage = async (url, headers = undefined) => {
      if (!url) return null;
      const res = await fetch(url, headers ? { headers } : undefined);
      if (!res.ok) return null;
      const contentType = (res.headers.get('content-type') || '').toLowerCase();
      if (!contentType.startsWith('image/')) return null;
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    };

    const loadAuthPhoto = async () => {
      const token = localStorage.getItem('authToken');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : undefined;
      const docId = fotoPerfilDoc?.id_documento || fotoPerfilDoc?.id;

      const candidates = [];
      if (docId) {
        const downloadUrl = getUrlDescargaDocEstudiante(docId);
        if (authHeaders) candidates.push({ url: downloadUrl, headers: authHeaders });
        candidates.push({ url: downloadUrl });
      }
      if (fotoDocUrl) {
        candidates.push({ url: fotoDocUrl });
        if (authHeaders) candidates.push({ url: fotoDocUrl, headers: authHeaders });
      }
      if (fotoUrl) {
        candidates.push({ url: fotoUrl });
        if (authHeaders) candidates.push({ url: fotoUrl, headers: authHeaders });
      }

      for (const candidate of candidates) {
        try {
          const resolved = await tryFetchImage(candidate.url, candidate.headers);
          if (resolved) {
            objectUrl = resolved;
            if (active) setFotoUrlAuth(resolved);
            return;
          }
        } catch {
          // Intentar siguiente estrategia.
        }
      }

      if (active) setFotoUrlAuth('');
    };

    loadAuthPhoto();

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fotoPerfilDoc, fotoDocUrl, fotoUrl]);
  const [formData, setFormData] = useState({
    matricula: student.matricula || '',
    nombreCompleto: student.nombre_completo || '',
    cicloEscolar: student.ciclo_escolar || '2025',
    seccion: student.seccion || 'BACHILLERATO',
    tipoExpediente: '',
    fechaExpediente: new Date().toISOString().split('T')[0],
    asunto: '',
    descripcion: '',
    observaciones: ''
  });

  useEffect(() => {
    const loadDocumentos = async () => {
      if (!estudianteId) {
        setDocs([]);
        return;
      }
      try {
        const data = await getDocumentosEstudiante(estudianteId);
        const list = Array.isArray(data) ? data : (data?.results || []);
        setDocs(Array.isArray(list) ? list : []);
      } catch {
        setDocs([]);
      }
    };

    loadDocumentos();

    // Cargar expedientes del estudiante
    const expedientesMock = [
      {
        id: 1,
        tipo: 'Académico',
        fecha: '2025-01-15',
        asunto: 'Reconocimiento por excelencia académica',
        descripcion: 'El estudiante ha demostrado un desempeño sobresaliente en todas las materias del ciclo escolar.',
        observaciones: 'Se recomienda continuar con el mismo nivel de dedicación.'
      },
      {
        id: 2,
        tipo: 'Disciplinario',
        fecha: '2025-02-10',
        asunto: 'Reporte de conducta',
        descripcion: 'Incidente menor en el área de recreo.',
        observaciones: 'Se habló con el estudiante y los padres. Situación resuelta.'
      }
    ];
    
    setExpedientes(expedientesMock);
    
    if (expedientesMock.length > 0) {
      const exp = expedientesMock[0];
      setFormData(prev => ({
        ...prev,
        tipoExpediente: exp.tipo,
        fechaExpediente: exp.fecha,
        asunto: exp.asunto,
        descripcion: exp.descripcion,
        observaciones: exp.observaciones
      }));
    }
  }, [student, estudianteId]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      loadExpediente(newIndex);
    }
  };

  const handleNext = () => {
    if (currentIndex < expedientes.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      loadExpediente(newIndex);
    }
  };

  const loadExpediente = (index) => {
    const exp = expedientes[index];
    setFormData(prev => ({
      ...prev,
      tipoExpediente: exp.tipo,
      fechaExpediente: exp.fecha,
      asunto: exp.asunto,
      descripcion: exp.descripcion,
      observaciones: exp.observaciones
    }));
  };

  const handleNew = () => {
    setFormData(prev => ({
      ...prev,
      tipoExpediente: '',
      fechaExpediente: new Date().toISOString().split('T')[0],
      asunto: '',
      descripcion: '',
      observaciones: ''
    }));
    setCurrentIndex(-1);
  };

  const handleSave = () => {
    console.log('Guardando expediente:', formData);
    alert('Expediente guardado exitosamente');
  };

  const handleDelete = () => {
    if (window.confirm('¿Está seguro de eliminar este expediente?')) {
      console.log('Eliminando expediente');
      alert('Expediente eliminado');
    }
  };

  const handleRefresh = () => {
    console.log('Actualizando lista de expedientes');
    // Recargar expedientes desde la API
  };

  const handlePrint = async () => {
    try {
      await generateCredencialPDF({
        student,
        periodo: student.ciclo_escolar || student.ciclo_nombre || '',
        cicloIngreso: student.ciclo_escolar_nombre || student.ciclo_ingreso_nombre || student.ciclo_escolar || student.ciclo_nombre || '',
        consecutivo: '001',
        descargar: true,
      });
    } catch (err) {
      console.error('Error al generar credencial:', err);
      alert('No se pudo generar la credencial. Intente nuevamente.');
    }
  };

  const handleList = () => {
    console.log('Mostrando lista de expedientes');
    // Abrir modal con lista de expedientes
  };

  const handleSelectFotoClick = () => {
    if (fotoInputRef.current) fotoInputRef.current.click();
  };

  const handleFotoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const name = (file.name || '').toLowerCase();
    const isImage = /^image\//.test(file.type) || ['.jpg', '.jpeg', '.png', '.webp'].some((ext) => name.endsWith(ext));
    if (!isImage) {
      alert('Solo se permiten imágenes JPG, PNG o WEBP para la foto de perfil.');
      event.target.value = '';
      return;
    }

    if (!estudianteId) {
      alert('No se encontró el identificador del estudiante.');
      event.target.value = '';
      return;
    }

    setUploadingFoto(true);
    try {
      const resp = await subirFotoEstudiante(estudianteId, file);
      const uploadedUrl = resp?.archivo_url || '';
      setFotoUrlLocal(uploadedUrl || URL.createObjectURL(file));
      if (typeof onFotoChanged === 'function') {
        await onFotoChanged();
      }
      alert('Foto de perfil guardada correctamente.');
    } catch (err) {
      console.error('Error al subir foto de perfil:', err);
      alert(`No se pudo subir la foto: ${err?.response?.data?.error || err.message}`);
    } finally {
      setUploadingFoto(false);
      event.target.value = '';
    }
  };

  return (
    <div className={styles.expedienteContainer}>
      {/* Header con datos generales */}
      <div className={styles.expedienteHeader}>
        <div className={styles.expedienteHeaderGrid}>
          <div className={styles.expedienteField}>
            <label className={styles.expedienteLabel}>Matrícula</label>
            <input 
              type="text" 
              className={styles.expedienteInput}
              value={formData.matricula}
              disabled
            />
          </div>
          <div className={styles.expedienteField}>
            <label className={styles.expedienteLabel}>Ciclo escolar</label>
            <input 
              type="text" 
              className={styles.expedienteInput}
              value={formData.cicloEscolar}
              disabled
            />
          </div>
          <div className={styles.expedienteField}>
            <label className={styles.expedienteLabel}>Sección</label>
            <input 
              type="text" 
              className={styles.expedienteInput}
              value={formData.seccion}
              disabled
            />
          </div>
          <div className={styles.expedienteFotoContainer}>
            <div
              className={styles.expedienteFoto}
              onClick={handleSelectFotoClick}
              style={{ cursor: uploadingFoto ? 'not-allowed' : 'pointer' }}
              title="Cambiar imagen de perfil"
            >
              {fotoPerfilUrl && !fotoLoadError ? (
                <img
                  src={fotoPerfilUrl}
                  alt="Foto de perfil"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={() => setFotoLoadError(true)}
                />
              ) : (
                <span>(FOTO)</span>
              )}
            </div>
            <input
              ref={fotoInputRef}
              type="file"
              accept="image/*,.jpg,.jpeg,.png,.webp"
              style={{ display: 'none' }}
              onChange={handleFotoChange}
            />
            <button
              type="button"
              className={styles.expedienteActionButton}
              onClick={handleSelectFotoClick}
              disabled={uploadingFoto}
              style={{ marginTop: 8, width: '100%' }}
            >
              {uploadingFoto ? 'Subiendo...' : 'Seleccionar imagen'}
            </button>
          </div>
        </div>
        
        <div className={styles.expedienteHeaderGrid}>
          <div className={styles.expedienteField} style={{ gridColumn: 'span 3' }}>
            <input 
              type="text" 
              className={styles.expedienteInput}
              value={formData.nombreCompleto}
              disabled
            />
          </div>
        </div>

        <div className={styles.expedienteHeaderGrid}>
          <div className={styles.expedienteField}>
            <label className={styles.expedienteLabel}>Tipo de expediente</label>
            <select 
              className={styles.expedienteSelect}
              value={formData.tipoExpediente}
              onChange={(e) => handleInputChange('tipoExpediente', e.target.value)}
            >
              <option value="">Seleccione...</option>
              <option value="Académico">Académico</option>
              <option value="Disciplinario">Disciplinario</option>
              <option value="Médico">Médico</option>
              <option value="Administrativo">Administrativo</option>
              <option value="Otro">Otro</option>
            </select>
          </div>
          <div className={styles.expedienteField}>
            <label className={styles.expedienteLabel}>Fecha de expediente</label>
            <input 
              type="date" 
              className={styles.expedienteInput}
              value={formData.fechaExpediente}
              onChange={(e) => handleInputChange('fechaExpediente', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Contenido del expediente */}
      <div className={styles.expedienteContent}>
        <div className={styles.expedienteField}>
          <label className={styles.expedienteLabel}>Asunto</label>
          <input 
            type="text" 
            className={styles.expedienteInput}
            value={formData.asunto}
            onChange={(e) => handleInputChange('asunto', e.target.value)}
            placeholder="Ingrese el asunto del expediente"
          />
        </div>

        <div className={styles.expedienteField}>
          <label className={styles.expedienteLabel}>Descripción</label>
          <textarea 
            className={styles.expedienteTextarea}
            value={formData.descripcion}
            onChange={(e) => handleInputChange('descripcion', e.target.value)}
            rows="8"
            placeholder="Describa detalladamente el contenido del expediente"
          />
        </div>

        <div className={styles.expedienteField}>
          <label className={styles.expedienteLabel}>Observaciones</label>
          <textarea 
            className={styles.expedienteTextarea}
            value={formData.observaciones}
            onChange={(e) => handleInputChange('observaciones', e.target.value)}
            rows="4"
            placeholder="Observaciones adicionales"
          />
        </div>
      </div>

      {/* Botones de navegación y acciones */}
      <div className={styles.expedienteActions}>
        {/* Navegación */}
        <div className={styles.expedienteNavigation}>
          <button 
            className={styles.expedienteNavButton}
            onClick={handlePrevious}
            disabled={currentIndex <= 0}
            title="Anterior"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <span className={styles.expedienteCounter}>
            {expedientes.length > 0 ? `${currentIndex + 1} / ${expedientes.length}` : '0 / 0'}
          </span>
          
          <button 
            className={styles.expedienteNavButton}
            onClick={handleNext}
            disabled={currentIndex >= expedientes.length - 1}
            title="Siguiente"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Acciones */}
        <div className={styles.expedienteActionButtons}>
          <button 
            className={styles.expedienteActionButton}
            onClick={handleNew}
            title="Nuevo"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <button 
            className={styles.expedienteActionButton}
            onClick={handleDelete}
            title="Eliminar"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <button 
            className={styles.expedienteActionButton}
            onClick={handleRefresh}
            title="Actualizar"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 4V10H7M23 20V14H17M20.49 9C19.9828 7.56678 19.1209 6.28536 17.9845 5.27542C16.8482 4.26548 15.4745 3.55976 13.9917 3.22426C12.5089 2.88875 10.9652 2.93434 9.50481 3.35677C8.04437 3.77921 6.71475 4.56471 5.64 5.64L1 10M23 14L18.36 18.36C17.2853 19.4353 15.9556 20.2208 14.4952 20.6432C13.0348 21.0657 11.4911 21.1112 10.0083 20.7757C8.52547 20.4402 7.1518 19.7345 6.01547 18.7246C4.87913 17.7146 4.01717 16.4332 3.51 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <button 
            className={styles.expedienteActionButton}
            onClick={handlePrint}
            title="Imprimir"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9V2H18V9M6 18H4C3.46957 18 2.96086 17.7893 2.58579 17.4142C2.21071 17.0391 2 16.5304 2 16V11C2 10.4696 2.21071 9.96086 2.58579 9.58579C2.96086 9.21071 3.46957 9 4 9H20C20.5304 9 21.0391 9.21071 21.4142 9.58579C21.7893 9.96086 22 10.4696 22 11V16C22 16.5304 21.7893 17.0391 21.4142 17.4142C21.0391 17.7893 20.5304 18 20 18H18M6 14H18V22H6V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <button 
            className={styles.expedienteActionButton}
            onClick={handleList}
            title="Lista"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 6H21M8 12H21M8 18H21M3 6H3.01M3 12H3.01M3 18H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Botón Guardar */}
        <button 
          className={styles.expedienteSaveButton}
          onClick={handleSave}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
          </svg>
        </button>
      </div>

      {/* Nota al pie */}
      <div className={styles.expedienteFooter}>
        Navegue entre los expedientes del alumno
      </div>
    </div>
  );
}

export default ExpedienteAlumno;
